import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { GameComponentProps } from '../core/types';
import { GridCell } from '../../shared/grid/GridCell';
import { HintButton } from '../../shared/grid/HintButton';
import { SymbolPad } from '../../shared/grid/SymbolPad';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import { findQueensConflicts, isQueensComplete, queenIndices } from './logic';
import { isQueensPuzzle, QUEENS_CELL_COUNT, QUEENS_SIZE, type QueenCell } from './types';

type QueensSavedState = {
  version: 1;
  board: QueenCell[];
  selectedIndex: number;
  hintUsed: boolean;
  revealedWrong: number[];
};

function readSavedState(value: unknown): QueensSavedState {
  const fallback: QueensSavedState = {
    version: 1,
    board: Array.from({ length: QUEENS_CELL_COUNT }, () => 0),
    selectedIndex: 0,
    hintUsed: false,
    revealedWrong: [],
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<QueensSavedState>;
  const board =
    Array.isArray(candidate.board) && candidate.board.length === QUEENS_CELL_COUNT
      ? candidate.board.map((cell) => (cell === 1 || cell === 2 ? cell : 0))
      : fallback.board;
  const selectedIndex =
    Number.isInteger(candidate.selectedIndex) &&
    candidate.selectedIndex! >= 0 &&
    candidate.selectedIndex! < QUEENS_CELL_COUNT
      ? candidate.selectedIndex!
      : 0;
  const revealedWrong = Array.isArray(candidate.revealedWrong)
    ? candidate.revealedWrong.filter(
        (index): index is number =>
          Number.isInteger(index) && index >= 0 && index < QUEENS_CELL_COUNT && board[index] === 2,
      )
    : [];
  return {
    version: 1,
    board,
    selectedIndex,
    hintUsed: candidate.hintUsed === true || revealedWrong.length > 0,
    revealedWrong,
  };
}

export function QueensGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isQueensPuzzle(puzzle.payload))
    throw new Error('Queens received an invalid puzzle payload.');
  const queensPuzzle = puzzle.payload;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const [board, setBoard] = useState(initial.board);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [hintUsed, setHintUsed] = useState(initial.hintUsed);
  const [revealedWrong, setRevealedWrong] = useState(new Set(initial.revealedWrong));
  const [status, setStatus] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);
  const completionSent = useRef(false);
  const drag = useRef<{
    moved: boolean;
    touched: Set<number>;
    baseBoard: QueenCell[];
    baseWrong: Set<number>;
  } | null>(null);
  const suppressClick = useRef(false);
  const conflicts = useMemo(
    () => findQueensConflicts(board, queensPuzzle.regions),
    [board, queensPuzzle.regions],
  );

  const persist = useCallback(
    (
      nextBoard: QueenCell[],
      nextSelected: number,
      nextHintUsed: boolean,
      nextWrong: Set<number>,
    ) => {
      onStateChange({
        version: 1,
        board: nextBoard,
        selectedIndex: nextSelected,
        hintUsed: nextHintUsed,
        revealedWrong: [...nextWrong],
      });
    },
    [onStateChange],
  );

  const select = useCallback(
    (index: number, focus = false) => {
      setSelectedIndex(index);
      persist(board, index, hintUsed, revealedWrong);
      if (focus) {
        window.requestAnimationFrame(() => {
          gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus();
        });
      }
    },
    [board, hintUsed, persist, revealedWrong],
  );

  const setCell = useCallback(
    (index: number, value: QueenCell) => {
      if (disabled || board[index] === value) return;
      const nextBoard = [...board];
      nextBoard[index] = value;
      const nextWrong = new Set(revealedWrong);
      nextWrong.delete(index);
      setBoard(nextBoard);
      setSelectedIndex(index);
      setRevealedWrong(nextWrong);
      setStatus(value === 2 ? 'Queen placed' : value === 1 ? 'X placed' : 'Cell cleared');
      persist(nextBoard, index, hintUsed, nextWrong);
    },
    [board, disabled, hintUsed, persist, revealedWrong],
  );

  const cycle = useCallback(
    (index: number) => setCell(index, board[index] === 0 ? 1 : board[index] === 1 ? 2 : 0),
    [board, setCell],
  );

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || event.button > 0) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-grid-index]');
    const index = Number(target?.dataset.gridIndex);
    if (!Number.isInteger(index) || board[index] === 2) return;
    drag.current = {
      moved: false,
      touched: new Set([index]),
      baseBoard: [...board],
      baseWrong: new Set(revealedWrong),
    };
  };

  const extendDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = drag.current;
    if (!gesture) return;
    const pointerTarget =
      typeof document.elementFromPoint === 'function'
        ? document.elementFromPoint(event.clientX, event.clientY)
        : (event.target as HTMLElement);
    const target = pointerTarget?.closest<HTMLElement>('[data-grid-index]');
    const index = Number(target?.dataset.gridIndex);
    if (!Number.isInteger(index) || gesture.touched.has(index)) return;
    gesture.moved = true;
    gesture.touched.add(index);
    const nextBoard = [...gesture.baseBoard];
    const nextWrong = new Set(gesture.baseWrong);
    gesture.touched.forEach((cell) => {
      if (nextBoard[cell] !== 2) nextBoard[cell] = 1;
      nextWrong.delete(cell);
    });
    setBoard(nextBoard);
    setSelectedIndex(index);
    setRevealedWrong(nextWrong);
    setStatus('X notes painted');
    persist(nextBoard, index, hintUsed, nextWrong);
  };

  const finishDrag = useCallback(() => {
    if (drag.current?.moved) {
      suppressClick.current = true;
      window.setTimeout(() => {
        suppressClick.current = false;
      });
    }
    drag.current = null;
  }, []);

  const reset = useCallback(() => {
    if (disabled) return;
    const nextBoard = Array.from({ length: QUEENS_CELL_COUNT }, () => 0) as QueenCell[];
    const nextWrong = new Set<number>();
    setBoard(nextBoard);
    setSelectedIndex(0);
    setRevealedWrong(nextWrong);
    setStatus('Board reset');
    persist(nextBoard, 0, hintUsed, nextWrong);
  }, [disabled, hintUsed, persist]);

  const revealWrong = useCallback(() => {
    if (disabled) return;
    const solution = new Set(queensPuzzle.solution);
    const nextWrong = new Set(queenIndices(board).filter((index) => !solution.has(index)));
    if (!hintUsed) onCharge('reveal-wrong', 30_000, 'Reveal wrong queens');
    setHintUsed(true);
    setRevealedWrong(nextWrong);
    setStatus(
      `${nextWrong.size} wrong ${nextWrong.size === 1 ? 'queen' : 'queens'} found${hintUsed ? '' : ' · +30s'}`,
    );
    persist(board, selectedIndex, true, nextWrong);
  }, [board, disabled, hintUsed, onCharge, persist, queensPuzzle.solution, selectedIndex]);

  useEffect(() => {
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    return () => {
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  }, [finishDrag]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() === 'q') {
        event.preventDefault();
        setCell(selectedIndex, 2);
      } else if (event.key.toLowerCase() === 'x') {
        event.preventDefault();
        setCell(selectedIndex, 1);
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        setCell(selectedIndex, 0);
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        cycle(selectedIndex);
      } else if (isGridArrowKey(event.key)) {
        event.preventDefault();
        select(moveGridSelection(selectedIndex, event.key, QUEENS_SIZE, QUEENS_SIZE), true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycle, disabled, select, selectedIndex, setCell]);

  useEffect(() => {
    if (completionSent.current || !isQueensComplete(board, queensPuzzle.regions)) return;
    completionSent.current = true;
    onComplete({
      kind: 'solved',
      baseDeltaMs: hintUsed ? 0 : -5_000,
      message: 'Royal arrangement!',
      metadata: { hintUsed, queenCount: 6 },
    });
  }, [board, hintUsed, onComplete, queensPuzzle.regions]);

  return (
    <section className="logic-grid-game queens-game" aria-labelledby="queens-title">
      <p className="eyebrow">One queen per row, column, and color</p>
      <h1 id="queens-title" className="sr-only">
        Queens
      </h1>
      <div
        ref={gridRef}
        className="logic-grid queens-grid"
        role="grid"
        aria-label="Queens grid, 6 rows by 6 columns and 6 colored regions"
        onPointerDown={startDrag}
        onPointerOver={extendDrag}
        onPointerMove={extendDrag}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onPointerLeave={finishDrag}
        onClickCapture={(event) => {
          if (!suppressClick.current) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClick.current = false;
        }}
      >
        {board.map((value, index) => {
          const row = Math.floor(index / QUEENS_SIZE);
          const column = index % QUEENS_SIZE;
          const region = queensPuzzle.regions[index];
          const rightBoundary =
            column < QUEENS_SIZE - 1 && queensPuzzle.regions[index + 1] !== region;
          const bottomBoundary =
            row < QUEENS_SIZE - 1 && queensPuzzle.regions[index + QUEENS_SIZE] !== region;
          const conflict = conflicts.has(index);
          const wrong = revealedWrong.has(index);
          const state = value === 2 ? 'queen' : value === 1 ? 'X note' : 'empty';
          return (
            <GridCell
              key={index}
              index={index}
              label={`Row ${row + 1} column ${column + 1}, region ${region + 1}, ${state}${
                conflict ? ', conflict' : ''
              }${wrong ? ', revealed wrong' : ''}`}
              selected={selectedIndex === index}
              given={false}
              conflict={conflict}
              wrong={wrong}
              className={`logic-cell queens-cell queens-region-${region} ${
                rightBoundary ? 'queens-cell--region-right' : ''
              } ${bottomBoundary ? 'queens-cell--region-bottom' : ''}`}
              onSelect={select}
              onActivate={cycle}
              onDirectSelect={(cellIndex) => setCell(cellIndex, 2)}
            >
              <span aria-hidden="true">{value === 2 ? '♛' : value === 1 ? '×' : ''}</span>
              {(conflict || wrong) && <i aria-hidden="true">!</i>}
            </GridCell>
          );
        })}
      </div>
      <div className="logic-legend" aria-label="Queens controls legend">
        Tap cycles empty, X, queen. Drag across cells to paint X notes. Right-click or long-press
        places a queen.
      </div>
      <div className="sudoku-status" role="status" aria-live="polite">
        {status}
      </div>
      <SymbolPad
        options={[
          { value: 2, symbol: '♛', label: 'queen' },
          { value: 1, symbol: '×', label: 'X note' },
        ]}
        disabled={disabled}
        onValue={(value) => setCell(selectedIndex, value as QueenCell)}
        onErase={() => setCell(selectedIndex, 0)}
      />
      <div className="grid-actions">
        <button type="button" className="grid-action" disabled={disabled} onClick={reset}>
          Reset
        </button>
        <HintButton
          label="Reveal wrong"
          costSeconds={30}
          paid={hintUsed}
          disabled={disabled}
          onClick={revealWrong}
        />
      </div>
    </section>
  );
}
